import { createHashHistory } from 'history';
import { connectRoutes, history } from 'redux-first-router';
import restoreScroll from 'redux-first-router-restore-scroll';
import { createStore, combineReducers, compose, applyMiddleware } from 'redux';
import reduceReducers from 'reduce-reducers';
import { observer, observe } from 'redux-observers';
import querySerializer from 'query-string';

import {
  reducers as gallery,
  triggerAppSync,
  setSyncInterval,
  syncInstalledApps,
  direction
} from 'actions/gallery';
import { reducers as registry } from 'actions/registry';
import { reducers as installFlow } from 'actions/install-flow';
import installFlowReducer from 'reducers/install-flow';
import App from 'models/app';
import { fetchInstalledInterval, fetchDuringSyncInterval } from 'config';
import routesMap from 'routesMap';
import { PageRoutes } from './constants';
import { navigateOut } from './utils';

const {
  reducer: routeReducer,
  middleware: routeMiddleware,
  enhancer
} = connectRoutes(routesMap, {
  createHistory: createHashHistory,
  onBeforeChange(dispatch, getState, { action }) {
    /* eslint-disable */
    const {
      type: currentPageType,
      pathname: currentPathname,
      ...currentRest
    } = action.meta.location.current;
    const {
      type: previousPageType,
      pathname: prevPathname,
      ...prevRest
    } = action.meta.location.prev;
    /* eslint-enable */

    // location.kind === pop is used only when back is pressed
    // since there is no back option between the three main pages(device, clocks & apps)
    // if the back button is pressed between those pages it can only come from android
    // and we can safely exit the gallery at that point
    if (action.meta.location.kind === 'pop') {
      const mainPages = [
        PageRoutes.MINE_V2,
        PageRoutes.GALLERY_V2_CLOCKS,
        PageRoutes.GALLERY_V2_APPS
      ];

      if (
        mainPages.includes(currentPageType) &&
        mainPages.includes(previousPageType) &&
        !(prevRest.query && prevRest.query.focused)
      ) {
        navigateOut();
        return;
      }

      if (
        currentPageType === PageRoutes.MINE_V2 &&
        prevRest.query &&
        prevRest.query.focused
      ) {
        window.location.hash = 'v2/device';
      }
    }
    const { kind } = (action.meta && action.meta.location) || {};
    if (/back|next|pop/.test(kind)) {
      dispatch(direction('back'));
    }
  },
  onAfterChange(dispatch, getState, { action }) {
    // length is 1 only on the first page
    if (history().length === 1) {
      window.__firstPage = action.type;
    }
  },
  querySerializer,
  restoreScroll: restoreScroll({ manual: true })
});

function createAsyncThunkMiddleware(extraArgument) {
  return ({ dispatch, getState }) => (next) => (action) => {
    if (typeof action === 'function') {
      if (Object.getPrototypeOf(action).constructor.name === 'AsyncFunction') {
        return (async function() {
          await action(dispatch, getState, extraArgument);
        })();
      }

      if (
        Object.getPrototypeOf(action).constructor.name === 'GeneratorFunction'
      ) {
        const asy = require('async_polyfill');
        return asy(action)(dispatch, getState, extraArgument);
      }

      return action(dispatch, getState, extraArgument);
    }

    return next(action);
  };
}

const asyncThunk = createAsyncThunkMiddleware();

// Support Redux Devtools Chrome Extension
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const supportsUserTiming =
  'performance' in window && performance.mark && performance.measure;

// Add User Timing labels for actions
const timing = () => (next) => (action) => {
  if (supportsUserTiming) {
    performance.mark(`${action.type}_start`);
    const result = next(action);
    performance.mark(`${action.type}_end`);
    performance.measure(
      `${action.type}`,
      `${action.type}_start`,
      `${action.type}_end`
    );
    return result;
  } else {
    return next(action);
  }
};

const store = createStore(
  reduceReducers(
    combineReducers({
      gallery,
      registry,
      installFlow,
      location: routeReducer
    }),
    installFlowReducer
  ),
  {},
  composeEnhancers(
    enhancer,
    applyMiddleware(routeMiddleware),
    applyMiddleware(asyncThunk),
    applyMiddleware(timing)
  )
);

const installFlowObserver = observer(
  ({ installFlow } = {}) => installFlow,
  (dispatch, installFlow = {}) => {
    if (installFlow.requiresSync) {
      // We want to make sure this is triggered out of the event loop
      setTimeout(() =>
        dispatch(triggerAppSync({ requiresWifi: installFlow.requiresWifi }))
      );
    }

    if (installFlow.currentlySyncing) {
      dispatch(setSyncInterval(fetchDuringSyncInterval));
    } else {
      dispatch(setSyncInterval(fetchInstalledInterval));
    }
  }
);

const syncIntervalObserver = observer(
  ({ gallery: { syncInterval } } = {}) => syncInterval,
  (dispatch, currSyncInterval, prevSyncInterval) => {
    if (currSyncInterval !== prevSyncInterval) {
      dispatch(syncInstalledApps({ reset: true }));
    }
  }
);

const installFinishedObserver = observer(
  (state) => {
    const installedApps = state.registry.installedApps;
    let hasInstallingApp = false;
    for (const appId in installedApps) {
      const app = App.selectFromState(appId, state);
      if (!app) {
        continue;
      }
      if (app.isInstalling) {
        hasInstallingApp = true;
      }
    }
    return hasInstallingApp;
  },
  (dispatch, currHasInstallingApp, prevHasInstallingApp) => {
    if (
      typeof currHasInstallingApp !== 'undefined' &&
      prevHasInstallingApp &&
      !currHasInstallingApp
    ) {
      dispatch(syncInstalledApps({ getAppData: true }));
    }
  }
);

observe(store, [
  installFlowObserver,
  syncIntervalObserver,
  installFinishedObserver
]);

export default store;
